import {useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import Modal from '@/shared/components/Modal';
import Button from '@/shared/components/Button';
import {LOGIN, SIGN_UP} from '@/utils/constants';
import CreateUserAccountForm from './AuthorizationForms/CreateUserAccountForm';
import {POPUP_FORM} from './helpers';
import {FormDataContext, FormDataContextType} from './FormDataContext';
import LoginForm from '@/components/Organization/Login/LoginForm';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getUserInfo, userLogin} from '@/store/user';
import {LoginFormValues} from '@/components/Organization/Login/LoginForm/formConfiguration';
import {USER_TYPE} from '@/utils/enums';
import {UserService, userStorage} from '@/utils/services';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {actionsUserNotLoginedStyle} from './ActionUserNotLogined.styles';
import {getCurrentEventSelector} from '@/store/organization-event';

const ActionUserNotLogined = ({
  onClose,
  title,
  parentClassName,
}: {
  onClose?: () => void;
  title: string;
  parentClassName?: React.CSSProperties;
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {slug} = useParams();

  const event = useAppSelector(getCurrentEventSelector);

  const [isShowPopup, setIsShowPopup] = useState(false);
  const [currentForm, setCurrentForm] = useState<POPUP_FORM>(POPUP_FORM.SIGN_UP);

  const handleSubmitClickHandler = async (values: LoginFormValues) => {
    try {
      const {userInfo} = await dispatch(userLogin(values)).unwrap();

      if (userInfo && userInfo.id) {
        if (event?.id) {
          const {data} = await UserService.ensureUserHaveAttendeeIdentity(event.id);

          if (data.attendeeId) {
            await dispatch(getUserInfo(userInfo.id)).unwrap();
          }
        }

        userStorage.set('userType', USER_TYPE.USER);
        onLoginSuccess();
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const signUpClickHandler = () => changePopupFormHandler(POPUP_FORM.SIGN_UP);

  const closeModalClickHandler = () => {
    setIsShowPopup(false);
  };

  const changePopupFormHandler = (newForm: POPUP_FORM) => {
    setIsShowPopup(true);
    setCurrentForm(newForm);
  };

  const onLoginSuccess = () => {
    onClose && onClose();
    setIsShowPopup(false);

    const returnTo = sessionStorage.getItem('private_return_to');
    sessionStorage.removeItem('private_return_to');
    navigate(returnTo ?? `/${slug}`, {replace: true});
  };

  const formData = useMemo(() => {
    switch (currentForm) {
      case POPUP_FORM.SIGN_UP:
        return {
          title: SIGN_UP,
          formContent: <CreateUserAccountForm />,
        };

      case POPUP_FORM.LOGIN:
        return {
          title: LOGIN,
          formContent: (
            <LoginForm
              handleSubmitClickHandler={handleSubmitClickHandler}
              handleSignUpRedirect={signUpClickHandler}
              onSocialLoginSuccess={onClose}
              userType={USER_TYPE.USER}
            />
          ),
        };

      default:
        return {
          formContent: <CreateUserAccountForm />,
        };
    }
  }, [currentForm]);

  useEffect(() => {
    if (location.state?.openLogin) {
      changePopupFormHandler(POPUP_FORM.LOGIN);
    }
  }, [location.state]);

  const formDataContextValue: FormDataContextType = useMemo(() => {
    return {
      closeModalClickHandler,
      changePopupFormHandler,
    };
  }, [closeModalClickHandler, changePopupFormHandler]);

  const showLogInClickHandler = () => changePopupFormHandler(POPUP_FORM.LOGIN);

  return (
    <FormDataContext.Provider value={formDataContextValue}>
      <Box sx={actionsUserNotLoginedStyle.wrapper}>
        <Button onClick={showLogInClickHandler} title={title} size="large" sx={parentClassName} />

        <Modal onClose={closeModalClickHandler} open={isShowPopup} sx={actionsUserNotLoginedStyle.modal}>
          {formData.formContent}
        </Modal>
      </Box>
    </FormDataContext.Provider>
  );
};

export default ActionUserNotLogined;
